// ⚠ AUTO-GENERATED from components/cs/cast.js — DO NOT EDIT MANUALLY
// 수정은 마스터(components/cs/cast.js)에서 하고 'npm run cs:sync' 실행

// cast.jsx — 머니타운 IP cast adapter
// Display modes:
//   <CastAvatar id expr size />     — 원형 닉옆 아바타 (얼굴 크롭)
//   <CastPortrait id size mood />   — 전신 시그니처 포즈 카드
//   <CastChip id label role />      — 인라인 칩 "@황둥이 · 분석봇"
//
// 자산 시스템 — 2단계 마이그레이션 (2026-05-23 design doc 참조)
//   ① 분리 PNG (신규, 권장): /redesign-assets/char/<NN>-<id>/{sig,confident,surprised,focused,sad,sleepy,celebrate}.png
//      - sig.png = 투명 PNG 1024×1024, 이름 라벨 없음
//      - 6표정 = 흰배경 OK 512×512, 흉상 프레임, 얼굴 중앙 정렬
//   ② 폴백(레거시): /redesign-assets/char/<NN>-<id>-{sig,exp}.jpg
//      - exp.jpg = 3×2 그리드 표정 시트 (캐릭터별 매직 넘버로 셀 크롭)
//      - sig.jpg = 전신 + 하단 이름 라벨 (crop 0.85로 라벨 제거)
//
// 마이그 정책: 캐릭터별 hasNewAssets 플래그로 점진 토글. 모두 true 되면
// 폴백 분기 + EXPR_POS 매직 넘버 + jpg 22장 일괄 제거.
//
// 자산 작업 가이드: docs/specs/2026-05-23-character-assets-separation-design.md §4

import { useState, useEffect } from 'react';

// 타이핑 톤 박제 (2026-05-28) — 캐릭터별 채팅 마이크로인터랙션 속도 매핑
//   base       : ms per char (글자 사이 기본 delay)
//   jitter     : base ± jitter 범위 랜덤 (자연스러운 흔들림)
//   punctPause : 마침표·물음표·느낌표 다음 추가 휴식
//   commaPause : 쉼표·중점 다음 추가 휴식
//   spacePause : 띄어쓰기 다음 추가 휴식
//   preDots    : 타이핑 시작 전 "..." 점 깜빡 인터스티셜 (0 = 없음, ms = 표시 시간)
// 박제 근거: 각 캐릭터 role + MBTI + 시나리오 톤. 토비↔부기·콩이+록이·황둥이+여우니 듀오 정합.
// 너굴(12번)은 솟다앱 자산 적재 후 별도 PR로 영입 예정.

const CAST = {
  kkami: {
    no: "01", ko: "까미", en: "Kkami", species: "까마귀", mbti: "ENTP",
    dir: "/redesign-assets/char/01-kkami",
    legacySig: "/redesign-assets/char/01-kkami-sig.jpg",
    legacyExp: "/redesign-assets/char/01-kkami-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.40, x: 45, y: 56 },  // 효신님 튜너 결정값 (2026-05-25)
    role: "1화 게스트 · 자기 과시 투자자",
    tone: "#2A2A2A",
    typing: { base: 38, jitter: 28, punctPause: 250, commaPause: 130, spacePause: 30, preDots: 0 },  // ENTP — 발산·즉흥 jitter ↑
  },
  bbiyong: {
    no: "02", ko: "삐용이", en: "Bbi-yong", species: "병아리", mbti: "ENFP",
    dir: "/redesign-assets/char/02-bbiyong",
    legacySig: "/redesign-assets/char/02-bbiyong-sig.jpg",
    legacyExp: "/redesign-assets/char/02-bbiyong-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.35, x: 50, y: 45 },
    role: "속보 리포터 · @삐용이 봇",
    tone: "#FFEB99",
    typing: { base: 28, jitter: 32, punctPause: 200, commaPause: 100, spacePause: 20, preDots: 0 },
  },
  ryan: {
    no: "03", ko: "라이언", en: "Ryan", species: "사자", mbti: "ESTJ",
    dir: "/redesign-assets/char/03-ryan",
    legacySig: "/redesign-assets/char/03-ryan-sig.jpg",
    legacyExp: "/redesign-assets/char/03-ryan-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.25, x: 51, y: 61 },
    role: "헤르메스 동상 · 실용주의",
    tone: "#E5A24B",
    typing: { base: 44, jitter: 12, punctPause: 350, commaPause: 180, spacePause: 40, preDots: 0 },
  },
  kongi: {
    no: "04", ko: "콩이", en: "Kong-i", species: "다람쥐", mbti: "ISTJ",
    dir: "/redesign-assets/char/04-kongi",
    legacySig: "/redesign-assets/char/04-kongi-sig.jpg",
    legacyExp: "/redesign-assets/char/04-kongi-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.20, x: 50, y: 36 },
    // 콩이 폴백 시트는 라벨이 위쪽 → 얼굴 중심을 셀 하단으로 보정 (자산 적재 후 제거)
    expFaceY: 0.55,
    role: "개미와 베짱이 · 신중한 적립러",
    tone: "#B27145",
    typing: { base: 52, jitter: 18, punctPause: 460, commaPause: 280, spacePause: 80, preDots: 1100 },  // ISTJ — 결정 후 일관 jitter ↓ (머뭇은 preDots로)
  },
  roki: {
    no: "05", ko: "록이", en: "Rock-i", species: "사슴", mbti: "INFJ",
    dir: "/redesign-assets/char/05-roki",
    legacySig: "/redesign-assets/char/05-roki-sig.jpg",
    legacyExp: "/redesign-assets/char/05-roki-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.30, x: 56, y: 88 },
    role: "펀드매니저 · 황금알 거위",
    tone: "#C8A8E0",
    typing: { base: 58, jitter: 24, punctPause: 420, commaPause: 220, spacePause: 60, preDots: 600 },
  },
  tobi: {
    no: "06", ko: "토비", en: "Tobi", species: "토끼", mbti: "ESFP",
    dir: "/redesign-assets/char/06-tobi",
    legacySig: "/redesign-assets/char/06-tobi-sig.jpg",
    legacyExp: "/redesign-assets/char/06-tobi-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.45, x: 33, y: 86 },
    role: "단타 · 부기와 듀오",
    tone: "#F48BA8",
    typing: { base: 26, jitter: 28, punctPause: 180, commaPause: 90, spacePause: 20, preDots: 0 },  // ESFP — 즉흥·관계 jitter ↑
  },
  lumi: {
    no: "07", ko: "루미", en: "Lumi", species: "개", mbti: "ENFJ",
    dir: "/redesign-assets/char/07-lumi",
    legacySig: "/redesign-assets/char/07-lumi-sig.jpg",
    legacyExp: "/redesign-assets/char/07-lumi-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.40, x: 50, y: 56 },
    role: "그림자 변환 · 호러",
    tone: "#7A6BB0",
    typing: { base: 64, jitter: 30, punctPause: 600, commaPause: 320, spacePause: 100, preDots: 800 },  // ENFJ + 호러 — 황둥이 70보다 낮은 base(호러 무게감) + preDots 800으로 극적 인터스티셜
  },
  mio: {
    no: "08", ko: "미오", en: "Mio", species: "고양이", mbti: "INTJ",
    dir: "/redesign-assets/char/08-mio",
    legacySig: "/redesign-assets/char/08-mio-sig.jpg",
    legacyExp: "/redesign-assets/char/08-mio-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.05, x: 50, y: 28 },
    role: "관망자 · 처음 한마디",
    tone: "#4A4A4A",
    typing: { base: 50, jitter: 18, punctPause: 500, commaPause: 250, spacePause: 60, preDots: 400 },
  },
  yeouni: {
    no: "09", ko: "여우니", en: "Yeo-uni", species: "여우", mbti: "ENTP",
    dir: "/redesign-assets/char/09-yeouni",
    legacySig: "/redesign-assets/char/09-yeouni-sig.jpg",
    legacyExp: "/redesign-assets/char/09-yeouni-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.20, x: 47, y: 84 },
    role: "진짜 운영자 (메인 떡밥)",
    tone: "#E08555",
    typing: { base: 38, jitter: 28, punctPause: 320, commaPause: 170, spacePause: 40, preDots: 0 },  // ENTP — 활동적·예리·황둥이와 분리
  },
  hwangdoongi: {
    no: "10", ko: "황둥이", en: "Hwang-doong", species: "황소", mbti: "ENFJ",
    dir: "/redesign-assets/char/10-hwangdoongi",
    legacySig: "/redesign-assets/char/10-hwangdoongi-sig.jpg",
    legacyExp: "/redesign-assets/char/10-hwangdoongi-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.30, x: 48, y: 61 },
    role: "메인 마스코트 · @황둥이 분석봇",
    tone: "#FFB347",
    typing: { base: 70, jitter: 28, punctPause: 380, commaPause: 200, spacePause: 60, preDots: 0 },  // ENFJ + 호스트·분석봇 — 차분 톤 강조 (62→70)
  },
  bugi: {
    no: "11", ko: "부기", en: "Bu-gi", species: "거북이", mbti: "ISTP",
    dir: "/redesign-assets/char/11-bugi",
    legacySig: "/redesign-assets/char/11-bugi-sig.jpg",
    legacyExp: "/redesign-assets/char/11-bugi-exp.jpg",
    hasNewAssets: true,
    tune: { zoom: 1.10, x: 50, y: 58 },
    role: "장투 · 토비와 듀오",
    tone: "#86B5E0",
    typing: { base: 78, jitter: 22, punctPause: 550, commaPause: 300, spacePause: 80, preDots: 400 },
  },
};

// 타이핑 톤 미정의 캐릭터용 폴백 (방어적 호출)
const DEFAULT_TYPING = { base: 44, jitter: 20, punctPause: 320, commaPause: 160, spacePause: 45, preDots: 0 };

// 캐릭터별 tune 미지정 시 폴백 (튜너 페이지 기본값 동일)
const DEFAULT_TUNE = { zoom: 1.6, x: 50, y: 35 };

// 캐릭터별 sigTune 미지정 시 폴백 (transform 효과 0 = identity)
// 2026-05-25 자산 적재: sig.png 11장이 PIL crop + 효신님 포토피아 알파화로 통일됨 → 캐릭터별 sigTune 박제 불필요.
// 미래 신규 캐릭터·재튜닝 필요 시 캐릭터 객체에 sigTune 박으면 자동 적용 (시그니처 튜너 /_tools/signature-tune.html로 결정).
const DEFAULT_SIG_TUNE = { zoom: 1.0, x: 50, y: 50 };

// 표정 이름 → 폴백 시트의 (col, row) 좌표. 신규 자산 모드에서는 파일명으로만 사용.
const EXPR_POS = {
  confident: [0, 0],
  surprised: [1, 0],
  focused:   [2, 0],
  sad:       [0, 1],
  sleepy:    [1, 1],
  celebrate: [2, 1],
};

// MBTI → cast key (MBTI 결과 페이지 자동 매칭)
const MBTI_TO_CAST = {
  ENFJ: "hwangdoongi",
  ENFP: "bbiyong",
  ESTJ: "ryan",
  ISTJ: "kongi",
  INFJ: "roki",
  ISFJ: "kongi",
  ESFJ: "hwangdoongi",
  INFP: "roki",
  INTJ: "mio",
  INTP: "mio",  // 보조 매핑 (미오 본인 = INTJ, INTP 사용자는 유사 톤 mio로 추천)
  ENTJ: "ryan",
  ENTP: "kkami",
  ISTP: "bugi",
  ISFP: "roki",
  ESTP: "tobi",
  ESFP: "tobi",  // 토비 본인 = ESFP (v5 design doc 박제), 삐용이 본인은 ENFP
};

/* ─────────── CastAvatar — 투명 캐릭터 아바타 ─────────── */
// bare 기본값 true (2026-05-29 효신님 일괄 적용): 마스크·배경·테두리 X · 투명 PNG 원본 비율(object-fit:contain)
// 전 사용처(40+) 자동 투명 배경. 원형 마스크가 필요한 예외 케이스만 bare={false} 명시.
// ⚠ ring/ringColor는 bare 모드에서 무시되고 bare={false}에서만 테두리를 그린다.
//    2026-09-09: bare 경로에 남아 있던 무효 ring 23곳 제거(화면 변화 0 — 프로브로 렌더 동일 실측).
//    유효 사용처는 ToastHost(bare={false}) 1곳뿐이라 시그니처는 유지한다.
//    halo(색 후광)는 사용처 0 + design.md §G-3 "아바타 오라 색 glow 금지"로 같은 날 제거.
/** 아바타 썸네일 경로 — /char/04-kongi/celebrate.png → /char/04-kongi/thumb/celebrate.png
 *  토스트 프리로드(lib/preloadCast.ts)도 같은 규칙을 써야 하므로 export 한다. */
export function avatarThumbSrc(dir, expr) {
  return `${dir}/thumb/${expr}.png`;
}

function CastAvatar({ id, expr = "confident", size = 40, ring, ringColor, bare = true, style, onClick }) {
  const c = CAST[id];
  // useState/useEffect는 조건부 호출 금지 — early return 전에 호출
  const [forceLegacy, setForceLegacy] = useState(false);
  const [forceFull, setForceFull] = useState(false);   // 썸네일 404 → 512px 원본으로 되돌림
  // 아바타는 192px 썸네일(약 41KB)을 쓴다 — 512px 원본은 장당 210KB라 34px 원에 그리려고
  // 받기엔 무겁고, 토스트처럼 즉시 떠야 하는 곳에서 회색 원이 먼저 보였다(2026-09-02).
  // 썸네일 생성 = scripts/gen_avatar_thumbs.sh (웹·모바일 public 양쪽). 없으면 아래 onerror가 원본으로 되돌린다.
  const fullSrc = c ? `${c.dir}/${expr}.png` : '';
  const newSrc = c && !forceFull ? avatarThumbSrc(c.dir, expr) : fullSrc;
  const useLegacy = !c || !c.hasNewAssets || forceLegacy;

  // 신규 자산 사전 로드 — 404 시 원본 → 그래도 404면 legacy 시트로 2단 폴백
  //   (backgroundImage엔 onError가 없어 Image()로 미리 확인해야 한다)
  useEffect(() => {
    if (useLegacy) return;
    let cancelled = false;
    const img = new Image();
    img.onerror = () => {
      if (cancelled) return;
      if (!forceFull) setForceFull(true);   // 썸네일이 없다 → 원본으로
      else setForceLegacy(true);            // 원본도 없다 → 시트 크롭으로
    };
    img.src = newSrc;
    return () => { cancelled = true; };
  }, [newSrc, useLegacy, forceFull]);

  if (!c) return null;

  const borderW = ring ? Math.max(2, Math.round(size * 0.06)) : 0;
  const commonStyle = {
    width: size, height: size, borderRadius: "50%",
    background: "var(--cs-surface-2)",
    border: ring ? `${borderW}px solid ${ringColor || "var(--cs-line-2)"}` : "none",
    flexShrink: 0,
    cursor: onClick ? "pointer" : "default",
    overflow: "hidden",
    position: "relative",
    ...style,
  };

  if (useLegacy) {
    // 폴백 — 시트 좌표 크롭 (자산 적재 후 분기 제거 예정)
    const [col, row] = EXPR_POS[expr] || EXPR_POS.confident;
    const cellW = 512, cellH = 512;
    const faceDiam = (c.expFaceDiam || 0.72) * cellW;
    const faceX = (c.expFaceX || 0.5) * cellW;
    const faceY = (c.expFaceY || 0.40) * cellH;
    const scale = size / faceDiam;
    const bgW = 1536 * scale, bgH = 1024 * scale;
    const bgX = -(col * cellW + faceX) * scale + size / 2;
    const bgY = -(row * cellH + faceY) * scale + size / 2;
    return (
      <div onClick={onClick} style={{
        ...commonStyle,
        backgroundImage: `url("${c.legacyExp}")`,
        backgroundSize: `${bgW}px ${bgH}px`,
        backgroundPosition: `${bgX}px ${bgY}px`,
        backgroundRepeat: "no-repeat",
      }} title={c.ko} />
    );
  }

  // bare 옵션 — 마스크·배경·테두리 X · 투명 PNG 원본 비율 (object-fit:contain)
  if (bare) {
    return (
      <img
        onClick={onClick}
        src={newSrc}
        alt={c.ko}
        title={c.ko}
        draggable={false}
        style={{
          width: size,
          height: size,
          objectFit: 'contain',
          flexShrink: 0,
          cursor: onClick ? 'pointer' : 'default',
          ...style,
        }}
      />
    );
  }

  // 신규 — backgroundImage + 캐릭터별 tune(zoom·X·Y) 적용 (튜너 결정값)
  const tune = c.tune || DEFAULT_TUNE;
  return (
    <div onClick={onClick} style={{
      ...commonStyle,
      backgroundImage: `url("${newSrc}")`,
      backgroundSize: `${tune.zoom * 100}%`,
      backgroundPosition: `${tune.x}% ${tune.y}%`,
      backgroundRepeat: "no-repeat",
    }} title={c.ko} />
  );
}

/* ─────────── CastPortrait — 전신 시그니처 포즈 ─────────── */
function CastPortrait({ id, size = 200, mood = "neutral", style, hideCaption = true }) {
  const c = CAST[id];
  const [forceLegacy, setForceLegacy] = useState(false);
  if (!c) return null;

  const useLegacy = !c.hasNewAssets || forceLegacy;
  const moodBg = {
    neutral: "transparent",
    sad:     "var(--cs-fall-soft)",
    happy:   "var(--cs-gold-soft)",
    alert:   "var(--cs-alert-soft)",
    bull:    "var(--cs-bull-soft)",
  };

  if (useLegacy) {
    // 폴백 — 시그니처 jpg 하단 이름 라벨을 crop 0.85로 잘라냄
    const crop = hideCaption ? 0.85 : 1;
    return (
      <div style={{
        width: size, height: size * crop,
        background: moodBg[mood] || "transparent",
        borderRadius: 16,
        overflow: "hidden",
        position: "relative",
        flexShrink: 0,
        ...style,
      }}>
        <img src={c.legacySig} style={{
          width: "100%", height: "auto", display: "block",
          objectFit: "contain", objectPosition: "top center",
        }} alt={c.ko} draggable={false} />
      </div>
    );
  }

  // 신규 — 시그니처 튜너 결정값(sigTune)으로 transform 적용해서 라벨 crop.
  // (효신님 포토피아 배경 투명화 작업 전까지는 흰 자국 잔존 — 임시 상태)
  // 시그니처 튜너(/_tools/signature-tune.html)의 transform 공식과 일치.
  const sigTune = c.sigTune || DEFAULT_SIG_TUNE;
  const sigDx = 50 - sigTune.x;
  const sigDy = 50 - sigTune.y;
  return (
    <div style={{
      width: size, height: size,
      background: moodBg[mood] || "transparent",
      borderRadius: 16,
      overflow: "hidden",
      position: "relative",
      flexShrink: 0,
      ...style,
    }}>
      <img
        src={`${c.dir}/sig.png`}
        onError={() => setForceLegacy(true)}
        alt={c.ko}
        draggable={false}
        style={{
          width: "100%", height: "100%", display: "block",
          objectFit: "contain",
          objectPosition: "center",
          transform: `scale(${sigTune.zoom}) translate(${sigDx}%, ${sigDy}%)`,
          transformOrigin: "center center",
        }}
      />
    </div>
  );
}

/* ─────────── CastChip — 인라인 칩 "@황둥이 · 분석봇" ─────────── */
function CastChip({ id, expr = "confident", size = 18, label, role, color }) {
  const c = CAST[id];
  if (!c) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px 3px 3px", background: "var(--cs-surface-2)",
      borderRadius: 999, fontSize: 11.5, fontWeight: 600,
      color: color || "var(--cs-ink)",
    }}>
      <CastAvatar id={id} expr={expr} size={size + 4} />
      <span>{label || `@${c.ko}`}</span>
      {role && <span style={{ color: "var(--cs-mute)", fontWeight: 500 }}>· {role}</span>}
    </span>
  );
}

// MBTI 16유형 → 한국어 짧은 라벨 (커뮤니티 글 작성자 배지 등). 마스터 단일 출처(cs:sync 보존)
const MBTI_SHORT_LABEL = {
  ENTP: "공격형", ENTJ: "전략형", ENFP: "탐험형", ENFJ: "공동체형",
  ESTP: "단타형", ESTJ: "관리형", ESFP: "소비형", ESFJ: "가족형",
  INTP: "분석형", INTJ: "비전형", INFP: "신념형", INFJ: "통찰형",
  ISTP: "실행형", ISTJ: "저축형", ISFP: "미니멀형", ISFJ: "수호형",
};

export { CAST, EXPR_POS, MBTI_TO_CAST, MBTI_SHORT_LABEL, DEFAULT_TYPING, CastAvatar, CastPortrait, CastChip };
